import { Link } from 'react-router-dom';
import { MapPin, Phone, Clock, Instagram } from 'lucide-react';
import { trackAction } from '../lib/analytics';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer data-testid="footer" className="bg-[#1C1917] text-[#FAF6F0] relative">
      {/* Ornate Top Border */}
      <div className="h-[3px] bg-gradient-to-r from-transparent via-[#C9A227] to-transparent" />
      <div className="h-px bg-gradient-to-r from-transparent via-[#C9A227]/30 to-transparent mt-[2px]" />
      
      {/* Decorative Pattern */}
      <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23C9A227' fill-opacity='1'%3E%3Cpath d='M40 0L60 20L40 40L20 20L40 0zM0 40L20 60L0 80L0 40zM80 40L80 80L60 60L80 40zM40 40L60 60L40 80L20 60L40 40z'/%3E%3C/g%3E%3C/svg%3E")`
      }} />
      
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-16 md:gap-12">
          {/* Brand */}
          <div className="text-center md:text-left">
            <img 
              src="/geet-neon.png" 
              alt="Geet Fine Dining"
              className="h-20 w-auto mb-6 mx-auto md:mx-0"
            />
            <p className="font-['Cormorant_Garamond'] text-lg text-[#FAF6F0]/60 leading-relaxed mb-6 italic">
              "Where every meal is a journey through the royal courts of India"
            </p>
            <div className="inline-block border border-[#C9A227]/40 px-4 py-2">
              <p className="text-[#C9A227] text-xs tracking-[0.2em] uppercase">
                Best Indian Restaurant
              </p>
              <p className="text-[#FAF6F0]/50 text-[10px] tracking-wider">
                South Africa
              </p>
            </div>
          </div>

          {/* Navigation */}
          <div className="text-center">
            <h4 className="font-['Marcellus'] text-lg mb-8 text-[#C9A227] tracking-[0.2em]">
              EXPLORE
            </h4>
            <nav className="space-y-4">
              {[
                { name: 'Home', path: '/geet' },
                { name: 'Our Story', path: '/geet/about' },
                { name: 'The Menu', path: '/geet/menu' },
                { name: 'Gallery', path: '/geet/gallery' },
                { name: 'Contact', path: '/geet/contact' },
              ].map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="block font-['Cormorant_Garamond'] text-lg text-[#FAF6F0]/60 hover:text-[#C9A227] transition-colors duration-300 tracking-wide"
                >
                  {link.name}
                </Link>
              ))}
            </nav>
          </div>

          {/* Contact Info */}
          <div className="text-center md:text-right">
            <h4 className="font-['Marcellus'] text-lg mb-8 text-[#C9A227] tracking-[0.2em]">
              VISIT US
            </h4>
            <div className="space-y-6">
              <div className="flex items-start gap-3 justify-center md:justify-end">
                <p className="font-['Cormorant_Garamond'] text-lg text-[#FAF6F0]/60 text-right">
                  547 Fehrsen St, Brooklyn Mall<br />
                  Brooklyn, Pretoria, 0181
                </p>
                <MapPin size={18} className="text-[#C9A227] mt-1 flex-shrink-0" />
              </div>
              <div className="flex items-center gap-3 justify-center md:justify-end">
                <a 
                  href="tel:0124603199" 
                  className="font-['Cormorant_Garamond'] text-xl text-[#FAF6F0]/60 hover:text-[#C9A227] transition-colors tracking-wider"
                >
                  012 460 3199
                </a>
                <Phone size={18} className="text-[#C9A227] flex-shrink-0" />
              </div>
              <div className="flex items-start gap-3 justify-center md:justify-end">
                <p className="font-['Cormorant_Garamond'] text-lg text-[#FAF6F0]/60 text-right">
                  Mon - Sat: 10am - 10pm<br />
                  Sunday: 10am - 4pm
                </p>
                <Clock size={18} className="text-[#C9A227] mt-1 flex-shrink-0" />
              </div>
            </div>
          </div>
        </div>

        {/* Ornate Divider */}
        <div className="mt-16 mb-8 flex items-center justify-center gap-4">
          <div className="h-px w-24 bg-gradient-to-r from-transparent to-[#C9A227]/50" />
          <a
            href="https://www.instagram.com/geet_indianrestaurant/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-instagram"
            onClick={() => trackAction('instagram', 'geet')}
            aria-label="Geet on Instagram"
            className="text-[#C9A227] hover:text-[#FAF6F0] transition-colors duration-300"
          >
            <Instagram size={22} />
          </a>
          <div className="h-px w-24 bg-gradient-to-l from-transparent to-[#C9A227]/50" />
        </div>

        {/* Bottom Bar */}
        <div className="text-center">
          <p className="font-['Cormorant_Garamond'] text-sm text-[#FAF6F0]/40 tracking-wider">
            © {currentYear} Geet Indian Restaurant. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
